import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import "./Automatizacion.css";


import control from "./images/imagen6.png";
import motor from "./images/Motor.png";
import motorElectrico from "./images/MotorElectrico.png";
import panel from "./images/Panel.png";
import headerAutomation from "./images/AutomatizacionHe.png";

const AutomationPremium = () => {
  const { t } = useTranslation();
  const text = t("automationPage", { returnObjects: true });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // 1) Definimos qué productos existen y su imagen local
  const productDefs = [
    { slug: "controles", image: control },
    { slug: "motores-electricos", image: motor },
    { slug: "motores-bateria", image: motorElectrico },
    { slug: "panel-solar", image: panel },
  ];

  // 2) Combinamos textos localizados + imágenes
  const products = productDefs.map((def, i) => {
    const p = text.products[def.slug];
    return {
      id: i + 1,
      slug: def.slug,
      image: def.image,
      name: p.name,
      alt: p.alt,
      description: p.description,
      features: p.features || [],
      primaryCta: { label: p.primaryCtaLabel, href: p.primaryCtaHref },
      secondaryCta: p.secondaryCtaLabel
        ? { label: p.secondaryCtaLabel, href: p.secondaryCtaHref }
        : null,
    };
  });

  // JSON-LD (usa textos localizados)
  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: text.seo.itemListName,
    itemListElement: products.map((p, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: p.name,
    })),
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: text.seo.serviceName,
    provider: { "@type": "LocalBusiness", name: "RollerUp" },
    areaServed: "Costa Rica",
    brand: "RollerUp",
    serviceType: text.seo.serviceType,
    description: text.seo.serviceDescription,
  };

  return (
    <div className="automation-premium-container">
      {/* Fondo */}
      <div className="gradient-background" aria-hidden="true"></div>
      <header className="automation-header">
        <img
          src={headerAutomation}
          alt={
            text.aria?.headerAlt || "Automatización para persianas y cortinas"
          }
          className="automation-header-image"
          loading="eager"
        />
        <div className="automation-header-overlay">
          {/* ÚNICO H1 para SEO */}
          <h1 className="automation-header-title">{text.ui.titleMain}</h1>
          {/* 👇 Leyenda debajo del título */}
          {text.ui?.subtitle && (
            <p className="automation-header-subtitle">
              {text.ui.subtitle}
            </p>
          )}
        </div>
      </header>

      {/* Encabezado SEO */}
      <div className="content-wrapper">
        {/* Grid de productos */}
        <div
          className="products-grid"
          role="list"
          aria-label={text.aria.listLabel}
        >
          {products.map((product) => (
            <motion.article
              key={product.id}
              className={`product-card ${
                selectedProduct === product.id ? "expanded" : ""
              }`}
              whileHover={{
                y: -10,
                boxShadow: "0 15px 30px rgba(234, 98, 38, 0.2)",
              }}
              onHoverStart={() => setHoveredCard(product.id)}
              onHoverEnd={() => setHoveredCard(null)}
              onClick={() =>
                setSelectedProduct(
                  selectedProduct === product.id ? null : product.id
                )
              }
              layout
              role="listitem"
              aria-expanded={selectedProduct === product.id}
            >
              <div className="card-content">
                

                <motion.div className="image-container" layout>
                  <img src={product.image} alt={product.alt} />
                  <div className="image-overlay"></div>
                </motion.div>

                <div className="product-info">
                  <h3>{product.name}</h3>
                </div>

                <AnimatePresence>
                  {selectedProduct === product.id && (
                    <motion.div
                      className="expanded-content"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                      layout
                    >
                      <p className="product-description">
                        {product.description}
                      </p>

                      {product.features.length > 0 && (
                        <div className="features-list">
                          <h4>{text.ui.featuresTitle}</h4>
                          <ul>
                            {product.features.map((f, i) => (
                              <li key={i}>
                                <span
                                  className="feature-icon"
                                  aria-hidden="true"
                                >
                                  ✓
                                </span>
                                {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="card-ctas">
                        <a
                          href={product.primaryCta.href}
                          className="cta-button primary"
                          aria-label={`${product.primaryCta.label} — ${product.name}`}
                        >
                          {product.primaryCta.label}
                          <span className="arrow-icon">→</span>
                        </a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Badge inferior opcional */}
      <div
        className="premium-badge bottom"
        aria-label={text.ui.qualityBadgeAria}
      >
        <span>{text.ui.qualityBadge}</span>
      </div>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
      />
    </div>
  );
};

export default AutomationPremium;
